import axios, { AxiosRequestHeaders } from "axios";
import i18n from "i18next";

import { logout } from "store/auth/reducer";
import { store } from "store/store";

const APP_URL = process.env.REACT_APP_OMANA_URL;
const FORBIDDEN_PATH = "/forbidden";

const extractForbiddenMessage = (
  error: any
): { messageKey: string | null; message: string | null } => {
  const data = error?.response?.data;
  const directMessage = typeof data?.message === "string" ? data.message : null;
  const errors = data?.errors;

  let errorsMessage: string | null = null;
  if (errors && typeof errors === "object") {
    const values = Object.values(errors);

    for (const value of values) {
      if (typeof value === "string") {
        errorsMessage = value;
        break;
      }

      if (Array.isArray(value)) {
        const firstTextItem = value.find(
          (item: unknown): item is string => typeof item === "string"
        );

        if (firstTextItem) {
          errorsMessage = firstTextItem;
          break;
        }
      }
    }
  }

  const resolvedMessage = directMessage || errorsMessage;
  if (!resolvedMessage) {
    return { messageKey: null, message: null };
  }

  if (resolvedMessage.startsWith("messages.")) {
    return { messageKey: resolvedMessage, message: null };
  }

  return { messageKey: null, message: resolvedMessage };
};

export const apiCall = (additionalHeaders?: AxiosRequestHeaders) => {
  const instance = axios.create({
    baseURL: APP_URL,
    headers: {
      "X-Locale": i18n.language,
      "ngrok-skip-browser-warning": "true",
      ...additionalHeaders,
    },
  });

  instance.interceptors.request.use((config) => {
    const token = store.getState().auth.token;
    if (token && config.headers) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      const statusCode = error?.response?.status;

      if (statusCode === 401) {
        localStorage.setItem("loggedUser", "");
        store.dispatch(logout());
      }

      if (statusCode === 403 && window.location.pathname !== FORBIDDEN_PATH) {
        const { messageKey, message } = extractForbiddenMessage(error);

        sessionStorage.removeItem("forbiddenMessageKey");
        sessionStorage.removeItem("forbiddenMessage");

        if (messageKey) {
          sessionStorage.setItem("forbiddenMessageKey", messageKey);
        }

        if (message) {
          sessionStorage.setItem("forbiddenMessage", message);
        }

        window.location.assign(FORBIDDEN_PATH);
      }

      return Promise.reject(error);
    }
  );

  return instance;
};
